<script setup lang="ts">
import config from "@/assets/asset-config.json";
import { Animations, Categorey, Sex } from "@/enums/characterGeneratorEnums";
import type {
  DefinitionConfig,
  DefinitionRecord,
  IAssetConfig,
} from "@/types/definiation-config";
import { SelectedItems, SheetDefinition } from "@/types/sheet-definiation";
import { computed } from "vue";

// Constants
const AssetConfig = config as IAssetConfig[];
const LEVEL_1_CATEGORIES_MAP: Record<string, IAssetConfig> = AssetConfig.map(
  (item) => item
).reduce((acc, curr) => {
  acc[curr.label] = curr;
  return acc;
}, {} as Record<string, IAssetConfig>);

// Props & Emits
const props = withDefaults(
  defineProps<{
    selectedSex: Sex; // 选中的性别，用于数据过滤
    selectedItems?: SelectedItems;
    selectedCategory: Categorey; // 选中的分类，用于数据过滤
    animationFilter?: Animations[];
  }>(),
  {
    selectedSex: Sex.Male,
    selectedItems: () => ({} as SelectedItems),
    selectedCategory: Categorey.Body,
    animationFilter: () => [],
  }
);

const emit = defineEmits<{
  (e: "update:selectedCategory", category: string): void;
  (e: "update:selectedItems", items: SelectedItems): void;
  (
    e: "update:focusingDefinition",
    definition: {
      definition?: DefinitionRecord;
      type?: string;
      valid: boolean;
    }
  ): void;
}>();

const assetConfig = computed<IAssetConfig>(() => {
  return LEVEL_1_CATEGORIES_MAP[props.selectedCategory];
});

// Methods

/**
 * 点击SheetDefinition
 * @param def
 */
const onClickSheetDefinition = (def: DefinitionRecord, type: string) => {
  if (def.definition.variants.length > 1 || def.accessories?.length) {
    emit("update:focusingDefinition", {
      definition: def,
      type,
      valid: true,
    });
  } else {
    onSelectSheetDefinition(def.definition, type);
  }
};

/**
 * 选中SheetDefinition
 * @param def
 */
const onSelectSheetDefinition = (
  def: SheetDefinition,
  type: string,
  variantIdx: number = 0
) => {
  const old = props.selectedItems[props.selectedCategory]?.[type];
  const isSame =
    old && old.name === def.name && old.selectedVariant === variantIdx;
  if (isSame) {
    emit("update:selectedItems", {
      ...props.selectedItems,
      [props.selectedCategory]: {
        ...(props.selectedItems[props.selectedCategory] ?? {}),
        [type]: {},
      },
    });
  } else {
    emit("update:selectedItems", {
      ...props.selectedItems,
      [props.selectedCategory]: {
        ...(props.selectedItems[props.selectedCategory] ?? {}),
        [type]: {
          ...def,
          selectedVariant: variantIdx,
        },
      },
    });
  }
};

const isSelected = (
  def: SheetDefinition,
  type: string,
  variantIdx?: number
) => {
  const target = props.selectedItems[props.selectedCategory]?.[type];
  if (variantIdx !== undefined) {
    return target?.name === def.name && target?.selectedVariant === variantIdx;
  }
  return target?.name === def.name;
};

const filteredDefinitions = (
  defRecords: DefinitionRecord[]
): DefinitionRecord[] => {
  const filteredDefinitions: DefinitionRecord[] = [];
  for (const def of defRecords) {
    if (def.definition["layer_1"].custom_animation) continue;
    if (props.animationFilter.length === 0) {
      filteredDefinitions.push(def);
    } else {
      for (const animation of props.animationFilter) {
        if (def.definition.animations?.includes(animation)) {
          filteredDefinitions.push(def);
          break;
        }
      }
    }
  }
  return filteredDefinitions;
};
</script>
<template>
  <div class="flex flex-col gap-2">
    <template v-if="assetConfig.type === 'category'">
      <template v-for="item in assetConfig.children" :key="item.label">
        <div
          v-if="item.type === 'definition'"
          class="bg-base-200 p-2 rounded-lg flex flex-col gap-2"
        >
          <div class="red-dot ml-2">
            {{ $t(`characterGenerator.itemName.${item.label}`) }}
          </div>
          <div class="grid grid-cols-5 gap-2">
            <DefiniationItem
              v-for="def in filteredDefinitions(item.items)"
              :key="`${def.definition.name}-${selectedSex}`"
              :definition="def.definition"
              :is-selected="isSelected(def.definition, item.label)"
              :selected-sex="selectedSex"
              @click="onClickSheetDefinition(def, item.label)"
            />
          </div>
        </div>
        <div v-else class="bg-base-200 p-2 rounded-lg flex flex-col gap-2">
          <div class="red-dot ml-2">
            {{ $t(`characterGenerator.itemName.${item.label}`) }}
          </div>
          <div
            v-for="defs in item.children"
            :key="`${defs.label}-${selectedSex}`"
            class="bg-base-300 p-2 rounded flex flex-col gap-2"
          >
            <div class="font-semibold red-dot ml-4">
              {{ $t(`characterGenerator.itemName.${defs.label}`) }}
            </div>
            <div class="grid grid-cols-5 gap-2">
              <DefiniationItem
                v-for="def in filteredDefinitions((defs as DefinitionConfig).items)"
                :key="`${def.definition.name}-${selectedSex}`"
                :definition="def.definition"
                :is-selected="isSelected(def.definition, defs.label)"
                :selected-sex="selectedSex"
                @click="onClickSheetDefinition(def, defs.label)"
              />
            </div>
          </div>
        </div>
      </template>
    </template>
    <template v-else>
      <div class="grid grid-cols-5 gap-2">
        <DefiniationItem
          v-for="def in filteredDefinitions(assetConfig.items)"
          :key="`${def.definition.name}-${selectedSex}`"
          :definition="def.definition"
          :is-selected="isSelected(def.definition, assetConfig.label)"
          :selected-sex="selectedSex"
          @click="onClickSheetDefinition(def, assetConfig.label)"
        />
      </div>
    </template>
  </div>
</template>
